import { useCallback, useEffect, useRef, useState } from 'react';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { usePresence } from '@/hooks/usePresence';
import { track } from '@/lib/analytics';
import { coalesceRevisions, type RevisionGroup } from '@/model/diff';
import { historyClient } from '@/persistence/historyClient';
import { useFunnelStore } from '@/store/useFunnelStore';
import { ChangeList } from './ChangeList';

interface Props {
  scenarioId: string | null;
}

/** Keep in step with --drawer-anim in index.css. */
const ANIMATION_MS = 240;

type LoadState =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'ready'; groups: RevisionGroup[] }
  | { phase: 'error'; message: string };

/**
 * The change history for one scenario, as a right-hand slide-in.
 *
 * Structurally a sibling of StageDrawer, reusing the same presence, focus trap
 * and Esc handling rather than inventing a second set. The store keeps the two
 * mutually exclusive, so the layer stack stays two deep (panel, then modal) and
 * Esc precedence is unchanged.
 */
export function HistoryPanel({ scenarioId }: Props) {
  const historyOpen = useFunnelStore((s) => s.historyOpen);
  const closeHistory = useFunnelStore((s) => s.closeHistory);
  const hydrate = useFunnelStore((s) => s.hydrate);
  const setStatus = useFunnelStore((s) => s.setStatus);

  const panelRef = useRef<HTMLElement>(null);
  const { mounted, active } = usePresence(historyOpen, ANIMATION_MS);
  const [state, setState] = useState<LoadState>({ phase: 'idle' });
  const [restoring, setRestoring] = useState<number | null>(null);

  useFocusTrap(panelRef, historyOpen && mounted);

  useEffect(() => {
    if (!historyOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeHistory();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [historyOpen, closeHistory]);

  const load = useCallback(async () => {
    if (!historyClient || !scenarioId) return;
    setState({ phase: 'loading' });
    try {
      const revisions = await historyClient.listRevisions(scenarioId);
      setState({ phase: 'ready', groups: coalesceRevisions(revisions) });
    } catch (error) {
      setState({
        phase: 'error',
        message: error instanceof Error ? error.message : 'Could not load history',
      });
    }
  }, [scenarioId]);

  // Refetch on every open: the log has grown since last time by definition.
  useEffect(() => {
    if (!historyOpen) return;
    void load();
  }, [historyOpen, load]);

  async function restore(version: number) {
    if (!historyClient || !scenarioId) return;
    setRestoring(version);
    try {
      const restored = await historyClient.restore(scenarioId, version);
      /* `hydrate` deliberately preserves `revision`, so this does NOT trip the
         autosave — the server has already recorded the restore as its own entry
         and a write-back would append a duplicate. */
      hydrate(restored.params);
      setStatus('saved');
      track('revision_restored', { version, to_version: restored.version });
      await load();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not restore';
      setStatus('error', message);
      track('restore_failed', { version, message });
      setState({ phase: 'error', message });
    } finally {
      setRestoring(null);
    }
  }

  if (!mounted) return null;

  const openClass = active ? ' is-open' : '';

  return (
    <>
      <div className={`drawer-backdrop${openClass}`} onClick={closeHistory} aria-hidden="true" />

      <aside
        ref={panelRef}
        className={`editor-drawer history-panel${openClass}`}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="history-title"
      >
        <header className="drawer-header">
          <div>
            <span className="drawer-kicker">This scenario</span>
            <h2 id="history-title">Change history</h2>
          </div>
          <button
            type="button"
            className="drawer-close"
            onClick={closeHistory}
            aria-label="Close change history"
          >
            ×
          </button>
        </header>

        <div className="drawer-body">
          <PanelBody
            state={state}
            scenarioId={scenarioId}
            restoring={restoring}
            onRestore={restore}
            onRetry={() => void load()}
          />
        </div>
      </aside>
    </>
  );
}

function PanelBody({
  state,
  scenarioId,
  restoring,
  onRestore,
  onRetry,
}: {
  state: LoadState;
  scenarioId: string | null;
  restoring: number | null;
  onRestore: (version: number) => void;
  onRetry: () => void;
}) {
  /* Says the true thing rather than showing an empty timeline: with no API
     configured the app is local-only, and a local scenario has no history to
     have. */
  if (!historyClient) {
    return (
      <p className="drawer-intro">
        Change history needs the scenario service. This session is saving to this
        browser only, so there is no server-side history to show.
      </p>
    );
  }

  if (!scenarioId) {
    return <p className="drawer-intro">Open a scenario to see its history.</p>;
  }

  if (state.phase === 'loading' || state.phase === 'idle') {
    return (
      <p className="loading" role="status">
        Loading history…
      </p>
    );
  }

  if (state.phase === 'error') {
    return (
      <>
        <p className="drawer-status is-error" role="status">
          {state.message}
        </p>
        <button type="button" className="drawer-reset" onClick={onRetry}>
          Try again
        </button>
      </>
    );
  }

  if (state.groups.length === 0) {
    return <p className="drawer-intro">No changes recorded yet.</p>;
  }

  return (
    <ol className="history-list">
      {state.groups.map((group, index) => (
        <HistoryEntry
          key={group.id}
          group={group}
          // The newest entry is the state on screen, so there is nothing to
          // return to.
          isCurrent={index === 0}
          restoring={restoring === group.version}
          disabled={restoring !== null}
          onRestore={() => onRestore(group.version)}
        />
      ))}
    </ol>
  );
}

function HistoryEntry({
  group,
  isCurrent,
  restoring,
  disabled,
  onRestore,
}: {
  group: RevisionGroup;
  isCurrent: boolean;
  restoring: boolean;
  disabled: boolean;
  onRestore: () => void;
}) {
  return (
    <li className={`history-entry${isCurrent ? ' is-current' : ''}`}>
      <div className="history-entry__head">
        <time dateTime={group.at} className="history-entry__time">
          {formatWhen(group.at)}
        </time>
        <span className="history-entry__version">
          {isCurrent ? 'Current' : `v${group.version}`}
        </span>
      </div>

      <p className="history-entry__summary">{summarize(group)}</p>

      {group.changes.length > 0 && <ChangeList changes={group.changes} />}

      {group.revisionCount > 1 && (
        <p className="history-entry__note">
          {group.revisionCount} autosaves, v{group.earliestVersion}–v{group.version}
        </p>
      )}

      {!isCurrent && (
        <button
          type="button"
          className="history-entry__restore"
          onClick={onRestore}
          disabled={disabled}
        >
          {restoring ? 'Restoring…' : `Restore v${group.version}`}
        </button>
      )}
    </li>
  );
}

function summarize(group: RevisionGroup): string {
  if (group.op === 'create') return 'Scenario created';
  if (group.op === 'reset') return 'Reset to defaults';
  if (group.op === 'restore') return `Restored version ${group.restoredFrom ?? '?'}`;
  if (group.changes.length === 0) return 'Saved with no visible change';
  return group.changes.length === 1 ? '1 change' : `${group.changes.length} changes`;
}

const WHEN = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

function formatWhen(iso: string): string {
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? '—' : WHEN.format(new Date(parsed));
}
